import {
  faCreditCard,
  faDragon,
  faMessage,
  faReceipt,
  faVideo,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { FlexBox } from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"

import { PlanTitle } from "./PlanTitle"
import { usePlanCard } from "./usePlanCard"
import TextArrowButton from "../../../../components/coaching/TextArrowButton"
import { COACH_PLANS, CoachPaywallChoice } from "../../../../constants"
import useMobile from "../../../../hooks/useMobile"
import { useCoachSubscriptionPackagesQuery } from "../../../../networking/coaching/queries"
import { CoachingLookupKey } from "../../../../types/coachingGroups"
import { localStorageItems } from "../../../../util/storage"
import { formattedCentsToDollars } from "../../../../util/util"
import { useCoachingStore } from "../../useCoachingStore"

const PlanInfoCard = () => {
  const defaultCoachPlanLookupKey = CoachingLookupKey.ONE_CALL

  const { t } = useTranslation()
  const { selectedCoach } = useCoachingStore()
  const { data: subscriptionPackages } = useCoachSubscriptionPackagesQuery()
  const { redirectToCoachCheckout, isLoading } = usePlanCard()
  const coachingDefaultPlan = subscriptionPackages?.[defaultCoachPlanLookupKey]
  const isMobile = useMobile()
  const { plan, frequency } = COACH_PLANS[defaultCoachPlanLookupKey]
  const firstMonthDiscount = coachingDefaultPlan?.first_month_discount
  const priceInCents = coachingDefaultPlan?.unit_amount ?? 0

  const priceForToday = formattedCentsToDollars(
    firstMonthDiscount?.unit_amount ?? priceInCents
  )
  const fullPrice = formattedCentsToDollars(
    firstMonthDiscount?.unit_amount ? priceInCents : priceInCents * 2
  )

  const priceLabel =
    firstMonthDiscount !== null
      ? t("COACHING_PLAN_AFTERWARDS", { price: fullPrice })
      : t("COACHING_PLAN_EARLY_BIRD")

  const bundleJoonity =
    localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
      CoachPaywallChoice.WITH_COACH ||
    localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
      CoachPaywallChoice.EXIT_WITH_COACH

  const infoCards = [
    ...(bundleJoonity
      ? [
          {
            icon: faDragon,
            text: t("PAYWALL_BUNDLING_FULL_ACCESS_INCLUDED"),
          },
        ]
      : []),
    {
      icon: faMessage,
      text: t("PAYWALL_BUNDLING_MESSAGING"),
    },
    {
      icon: faVideo,
      text: frequency,
    },
    {
      icon: faCreditCard,
      text: t("PAYWALL_BUNDLING_HSA"),
    },
    {
      icon: faReceipt,
      text: t("COACHING_PLAN_RISK_FREE"),
    },
  ].filter((card) => !!card.text)

  return (
    <FlexBox
      direction="column"
      justify="center"
      align="center"
      gap={SPACING.space0}
      style={{
        width: "min(100%, 400px)",
        opacity: subscriptionPackages ? 1 : 0,
        background: "#F9FAFC",
        border: `2px solid ${JoonUIColor.text.primaryAccent}`,
        borderRadius: "16px",
        marginBottom: SPACING.space4,
        boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.10)",
        transition: "opacity .3s ease-in-out",
      }}
    >
      <FlexBox
        justify="center"
        align="center"
        gap={SPACING.space1}
        style={{
          padding: SPACING.space4,
        }}
      >
        <FlexBox
          direction="row"
          justify="space-between"
          align="center"
          fullWidth
          wrap={false}
        >
          <FlexBox
            justify="flex-start"
            align="center"
            gap={SPACING.space1}
            wrap={false}
          >
            <PlanTitle plan={plan} coachingPlan={defaultCoachPlanLookupKey} />
          </FlexBox>

          {firstMonthDiscount ? (
            <FlexBox justify="flex-end" align="center" gap={SPACING.space2}>
              <Typography
                variant="h2"
                color={JoonUIColor.background.disabled}
                style={{ textDecoration: "line-through" }}
              >
                ${fullPrice}
              </Typography>
              <Typography variant="h2" color={JoonUIColor.text.primary}>
                ${priceForToday}
              </Typography>
            </FlexBox>
          ) : (
            <FlexBox justify="flex-end" align="center" gap={SPACING.space2}>
              <Typography variant="h2" color={JoonUIColor.text.primary}>
                ${priceForToday}
                <Typography variant="body" color={JoonUIColor.text.primary}>
                  {t("COACHING_PLAN_PER_MONTH")}
                </Typography>
              </Typography>
            </FlexBox>
          )}
        </FlexBox>

        {firstMonthDiscount && (
          <>
            <FlexBox
              style={{
                padding: `${SPACING.space1} ${SPACING.space2}`,
                background: JoonColorExpanded.yellow300,
                borderRadius: SPACING.space1,
              }}
              fullWidth
            >
              <Typography
                variant="bodyXSmallBold"
                color={JoonUIColor.text.primary}
                textAlign="center"
                style={{ whiteSpace: "nowrap" }}
              >
                {t("COACHING_PLAN_FIRST_MONTH", {
                  percent: firstMonthDiscount.percentage_off,
                })}
              </Typography>
            </FlexBox>

            <Typography
              variant="bodyXSmallBold"
              color={JoonUIColor.text.secondary}
            >
              {priceLabel}
            </Typography>
          </>
        )}
      </FlexBox>

      <FlexBox
        direction="column"
        justify="space-between"
        align="flex-start"
        gap={SPACING.space2}
        wrap={false}
        style={{
          padding: `${SPACING.space4} ${SPACING.space2}`,
          background: JoonUIColor.background.primaryNeutral,
          borderTop: `1px solid ${JoonUIColor.icon.light}`,
          borderBottom: `1px solid ${JoonUIColor.icon.light}`,
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
        }}
      >
        <FlexBox direction="column" align="flex-start" gap={SPACING.space0}>
          {infoCards.map(({ icon, text }, index) => (
            <FlexBox
              key={index}
              direction="row"
              justify="flex-start"
              align="center"
              gap={SPACING.space2}
              wrap={false}
              style={{ padding: SPACING.space2 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: "20px",
                  maxWidth: "20px",
                }}
              >
                <FontAwesomeIcon
                  icon={icon}
                  color={JoonUIColor.icon.accent}
                  fontSize={16}
                />
              </div>
              <Typography
                variant={isMobile ? "bodyXSmall" : "bodySmall"}
                color={JoonUIColor.text.primary}
                style={{ textAlign: "left", whiteSpace: "pre-line" }}
              >
                {text}
              </Typography>
            </FlexBox>
          ))}
        </FlexBox>

        <div
          style={{ width: "100%" }}
          onPointerDown={(e) => {
            e.currentTarget.dataset.pressed = "true"
            e.currentTarget.dataset.pressX = e.clientX.toString()
            e.currentTarget.dataset.pressY = e.clientY.toString()
          }}
          onPointerUp={async (e) => {
            if (e.currentTarget.dataset.pressed === "true") {
              const startX = Number(e.currentTarget.dataset.pressX || 0)
              const startY = Number(e.currentTarget.dataset.pressY || 0)
              const deltaX = Math.abs(e.clientX - startX)
              const deltaY = Math.abs(e.clientY - startY)
              if (deltaX < 10 && deltaY < 10)
                redirectToCoachCheckout(
                  defaultCoachPlanLookupKey,
                  priceInCents / 100
                )
            }
            e.currentTarget.dataset.pressed = "false"
          }}
          onPointerLeave={(e) => {
            e.currentTarget.dataset.pressed = "false"
          }}
        >
          <TextArrowButton
            text={t("COACHING_PLAN_GET_STARTED_WITH", {
              name: selectedCoach?.user.name || t("COACHING_PLAN_YOUR_COACH"),
            })}
            isLoading={isLoading}
            onClick={() => {}}
            style={{
              position: "relative",
              width: "100%",
              cursor: "pointer",
            }}
          />
        </div>
      </FlexBox>
    </FlexBox>
  )
}

export default PlanInfoCard
